import {
  Application,
  Container,
  Graphics,
  Sprite,
  Assets,
  FederatedPointerEvent,
  Rectangle,
} from 'pixi.js';
import type { LevelData } from '@shared/types/LevelTypes';
import type { EditorObject, EditorEntity, EditorMode } from '../stores/editorStore';
import { useEditorStore } from '../stores/editorStore';
import {
  normalizeAssetKey,
  resolveDecorationImage,
  resolveEntityImage,
} from '../utils/assetPreview';

export class PixiApp {
  private app: Application;
  private container: HTMLElement;
  private levelData: LevelData | null = null;
  private worldContainer: Container;
  private objectsContainer: Container;
  private entitiesContainer: Container;
  private gridContainer: Container;
  private sprites: Map<string, Sprite> = new Map();
  private selectionGraphics: Map<string, Graphics> = new Map();
  private boundsGraphics: Map<string, Graphics> = new Map();
  private initialized = false;
  private destroyed = false;
  private textureLoadVersion = 0;
  private loadedTextures: Set<string> = new Set();
  private loadingTextures: Set<string> = new Set();
  private alphaBoundsCache: Map<string, Uint8ClampedArray> = new Map();

  // Pan/Zoom state
  private scale = 0.1;
  private panX = 0;
  private panY = 0;
  private isPanning = false;
  private lastPanPoint = { x: 0, y: 0 };

  // Drag state
  private isDragging = false;
  private dragStartWorld = { x: 0, y: 0 };
  private dragStartPositions: Map<string, { x: number; y: number }> = new Map();

  // Selection box
  private selectionBox: Graphics;
  private isSelecting = false;
  private selectionStart = { x: 0, y: 0 };

  // Resize observer
  private resizeObserver: ResizeObserver | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    this.app = new Application();
    this.worldContainer = new Container();
    this.objectsContainer = new Container();
    this.entitiesContainer = new Container();
    this.gridContainer = new Container();
    this.selectionBox = new Graphics();
    this.init();
  }

  private async init() {
    await this.app.init({
      background: '#1a1a2e',
      width: this.container.clientWidth,
      height: this.container.clientHeight,
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    });

    if (this.destroyed) return;

    this.container.appendChild(this.app.canvas as HTMLCanvasElement);

    // Manual resize observer instead of resizeTo
    this.resizeObserver = new ResizeObserver(() => {
      if (this.initialized) {
        this.app.renderer.resize(this.container.clientWidth, this.container.clientHeight);
      }
    });
    this.resizeObserver.observe(this.container);

    // Build hierarchy
    this.worldContainer.addChild(this.gridContainer);
    this.worldContainer.addChild(this.objectsContainer);
    this.worldContainer.addChild(this.entitiesContainer);
    this.worldContainer.addChild(this.selectionBox);
    this.app.stage.addChild(this.worldContainer);

    // Set up event handlers
    this.setupInteraction();

    this.initialized = true;
    if (this.levelData) {
      this.setLevelData(this.levelData);
    } else {
      this.drawGrid();
    }
  }

  private setupInteraction() {
    this.app.stage.eventMode = 'static';
    this.app.stage.hitArea = this.app.screen;

    // Wheel zoom
    this.container.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        const newScale = Math.max(0.02, Math.min(2, this.scale * delta));

        // Zoom towards mouse position
        const rect = this.container.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        // Get world position under mouse before zoom
        const worldX = (mouseX - this.panX) / this.scale;
        const worldY = (mouseY - this.panY) / this.scale;

        this.scale = newScale;

        // Adjust pan to keep world position under mouse
        this.panX = mouseX - worldX * this.scale;
        this.panY = mouseY - worldY * this.scale;

        this.updateTransform();
      },
      { passive: false }
    );

    // Mouse events on stage
    this.app.stage.on('pointerdown', (e: FederatedPointerEvent) => {
      // Middle mouse: start pan
      if (e.button === 1) {
        this.isPanning = true;
        this.lastPanPoint = { x: e.globalX, y: e.globalY };
        return;
      }

      // Left mouse on empty space: start selection box
      if (e.button === 0) {
        const target = e.target as { objectId?: string };
        if (!target.objectId) {
          this.isSelecting = true;
          const worldPos = this.screenToWorld(e.globalX, e.globalY);
          this.selectionStart = worldPos;
          this.selectionBox.visible = true;

          // Clear selection unless shift
          if (!e.shiftKey) {
            useEditorStore.getState().clearSelection();
          }
        }
      }
    });

    this.app.stage.on('pointermove', (e: FederatedPointerEvent) => {
      // Pan
      if (this.isPanning) {
        const dx = e.globalX - this.lastPanPoint.x;
        const dy = e.globalY - this.lastPanPoint.y;
        this.panX += dx;
        this.panY += dy;
        this.lastPanPoint = { x: e.globalX, y: e.globalY };
        this.updateTransform();
        return;
      }

      // Selection box
      if (this.isSelecting) {
        const worldPos = this.screenToWorld(e.globalX, e.globalY);
        this.drawSelectionBox(this.selectionStart, worldPos);
        return;
      }

      // Drag selected objects - only update sprites visually
      if (this.isDragging) {
        const worldPos = this.screenToWorld(e.globalX, e.globalY);
        const dx = worldPos.x - this.dragStartWorld.x;
        const dy = worldPos.y - this.dragStartWorld.y;

        // Update sprite positions visually (not store)
        for (const [id, startPos] of this.dragStartPositions) {
          const sprite = this.sprites.get(id);
          if (sprite) {
            sprite.x = startPos.x + dx;
            sprite.y = startPos.y + dy;
          }
        }

        // Update selection graphics
        this.updateSelectionGraphics();
      }
    });

    this.app.stage.on('pointerup', (e: FederatedPointerEvent) => {
      // End pan
      if (this.isPanning) {
        this.isPanning = false;
      }

      // End selection box
      if (this.isSelecting) {
        this.isSelecting = false;
        this.selectionBox.visible = false;
        this.selectionBox.clear();

        // Find objects in selection box
        const worldPos = this.screenToWorld(e.globalX, e.globalY);
        const minX = Math.min(this.selectionStart.x, worldPos.x);
        const maxX = Math.max(this.selectionStart.x, worldPos.x);
        const minY = Math.min(this.selectionStart.y, worldPos.y);
        const maxY = Math.max(this.selectionStart.y, worldPos.y);

        if (maxX - minX > 5 && maxY - minY > 5) {
          const store = useEditorStore.getState();
          const objectsToSelect: string[] = [];

          if (store.mode === 'layouts') {
            for (const obj of store.objects) {
              if (
                obj.data.x >= minX &&
                obj.data.x <= maxX &&
                obj.data.y >= minY &&
                obj.data.y <= maxY
              ) {
                if (store.layerVisibility[obj.layerName]) {
                  objectsToSelect.push(obj.id);
                }
              }
            }
          } else {
            for (const entity of store.entities) {
              if (
                entity.data.x >= minX &&
                entity.data.x <= maxX &&
                entity.data.y >= minY &&
                entity.data.y <= maxY
              ) {
                objectsToSelect.push(entity.id);
              }
            }
          }

          if (objectsToSelect.length > 0) {
            if (e.shiftKey) {
              store.selectMultiple([...store.selectedIds, ...objectsToSelect]);
            } else {
              store.selectMultiple(objectsToSelect);
            }
          }
        }
      }

      // End drag - update store with final positions
      if (this.isDragging) {
        this.isDragging = false;

        // Restore boundsGraphics visibility
        const store = useEditorStore.getState();
        for (const [id, bounds] of this.boundsGraphics) {
          const sprite = this.sprites.get(id);
          const obj = store.objects.find((o) => o.id === id);
          if (sprite && obj) {
            // Restore visibility based on layer visibility state
            const isVisible = store.layerVisibility[obj.layerName] ?? true;
            bounds.visible = isVisible && sprite.visible;
          }
        }

        // Calculate final delta
        const worldPos = this.screenToWorld(e.globalX, e.globalY);
        const dx = worldPos.x - this.dragStartWorld.x;
        const dy = worldPos.y - this.dragStartWorld.y;

        // Only update if we actually moved
        if (Math.abs(dx) > 1 || Math.abs(dy) > 1) {
          // Commit the actual sprite positions to avoid stale start-position bugs.
          for (const [id] of this.dragStartPositions) {
            const sprite = this.sprites.get(id);
            if (sprite) {
              store.setObjectPosition(id, sprite.x, sprite.y, true);
            }
          }

          // Push history once after all updates
          store.pushHistory();
        }

        this.dragStartPositions.clear();
      }
    });
  }

  private screenToWorld(screenX: number, screenY: number): { x: number; y: number } {
    return {
      x: (screenX - this.panX) / this.scale,
      y: (screenY - this.panY) / this.scale,
    };
  }

  screenToWorldPoint(clientX: number, clientY: number): { x: number; y: number } {
    const rect = this.container.getBoundingClientRect();
    return this.screenToWorld(clientX - rect.left, clientY - rect.top);
  }

  private updateTransform() {
    this.worldContainer.scale.set(this.scale);
    this.worldContainer.x = this.panX;
    this.worldContainer.y = this.panY;
  }

  private drawSelectionBox(start: { x: number; y: number }, end: { x: number; y: number }) {
    this.selectionBox.clear();

    const x = Math.min(start.x, end.x);
    const y = Math.min(start.y, end.y);
    const width = Math.abs(end.x - start.x);
    const height = Math.abs(end.y - start.y);

    this.selectionBox.rect(x, y, width, height);
    this.selectionBox.fill({ color: 0xe94560, alpha: 0.1 });
    this.selectionBox.stroke({ width: 2 / this.scale, color: 0xe94560, alpha: 0.8 });
  }

  private drawGrid() {
    this.gridContainer.removeChildren();
    const graphics = new Graphics();

    const gridSize = 100;
    const width = 50000;
    const height = 3000;

    // Draw grid lines
    for (let x = 0; x <= width; x += gridSize) {
      graphics.moveTo(x, 0);
      graphics.lineTo(x, height);
    }

    for (let y = 0; y <= height; y += gridSize) {
      graphics.moveTo(0, y);
      graphics.lineTo(width, y);
    }

    graphics.stroke({ width: 1, color: 0x2a2a4a, alpha: 0.3 });

    // Draw origin axes
    graphics.moveTo(0, 0);
    graphics.lineTo(200, 0);
    graphics.stroke({ width: 2, color: 0xe94560, alpha: 0.5 });

    graphics.moveTo(0, 0);
    graphics.lineTo(0, 200);
    graphics.stroke({ width: 2, color: 0x4ade80, alpha: 0.5 });

    this.gridContainer.addChild(graphics);
  }

  setLevelData(levelData: LevelData) {
    this.levelData = levelData;

    if (!this.initialized) return;

    this.textureLoadVersion += 1;

    // Reset view
    this.scale = 0.1;
    this.panX = this.container.clientWidth / 2 - (levelData.width * this.scale) / 2;
    this.panY = this.container.clientHeight / 2 - (levelData.height * this.scale) / 2;
    this.updateTransform();

    // Redraw grid for level size
    this.drawLevelGrid();

    // Load textures for this level
    this.loadLevelTextures(levelData.id, this.textureLoadVersion);
  }

  private drawLevelGrid() {
    if (!this.levelData) return;

    this.gridContainer.removeChildren();
    const graphics = new Graphics();

    const minorGridSize = 32;
    const majorGridSize = 160;
    const width = this.levelData.width;
    const height = this.levelData.height;

    // Draw level bounds
    graphics.rect(0, 0, width, height);
    graphics.stroke({ width: 3, color: 0xe94560, alpha: 0.8 });

    // Draw minor grid
    for (let x = 0; x <= width; x += minorGridSize) {
      graphics.moveTo(x, 0);
      graphics.lineTo(x, height);
    }

    for (let y = 0; y <= height; y += minorGridSize) {
      graphics.moveTo(0, y);
      graphics.lineTo(width, y);
    }

    graphics.stroke({ width: 1, color: 0x24324f, alpha: 0.22 });

    // Draw major grid
    for (let x = 0; x <= width; x += majorGridSize) {
      graphics.moveTo(x, 0);
      graphics.lineTo(x, height);
    }

    for (let y = 0; y <= height; y += majorGridSize) {
      graphics.moveTo(0, y);
      graphics.lineTo(width, y);
    }

    graphics.stroke({ width: 1.5, color: 0x39527d, alpha: 0.35 });

    this.gridContainer.addChild(graphics);
  }

  private async loadLevelTextures(levelId: string, loadVersion: number) {
    try {
      const parseJsonSafe = async <T>(response: Response): Promise<T | null> => {
        if (!response.ok) return null;
        const raw = await response.text();
        const trimmed = raw.trim();
        // Vite dev fallback can return index.html with 200 for missing files.
        if (!trimmed || trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html')) {
          return null;
        }
        try {
          return JSON.parse(trimmed) as T;
        } catch {
          return null;
        }
      };

      // Load layout/entities base + overrides to derive textures (SSOT - no manifest)
      const [layoutRes, entitiesRes, layoutOverrideRes, entitiesOverrideRes] = await Promise.all([
        fetch(`/assets/levels/${levelId}-layout-c3data.json`),
        fetch(`/assets/levels/${levelId}-entities-c3data.json`),
        fetch(`/assets/levels/${levelId}-layout-overrides.json`),
        fetch(`/assets/levels/${levelId}-entities-overrides.json`),
      ]);

      if (this.destroyed || loadVersion !== this.textureLoadVersion) return;

      if (!layoutRes.ok && !entitiesRes.ok && !layoutOverrideRes.ok && !entitiesOverrideRes.ok)
        return;

      const layoutData = await parseJsonSafe<{
        layers: Array<{ objects: Array<{ type: string }>; tilemaps?: Array<{ type: string }> }>;
      }>(layoutRes);
      const entitiesData = await parseJsonSafe<{
        entities: Array<{ entityId: string; texture?: string }>;
      }>(entitiesRes);
      const layoutOverrideData = await parseJsonSafe<{
        layers?: Array<{ objects?: Array<{ type: string }>; tilemaps?: Array<{ type: string }> }>;
      }>(layoutOverrideRes);
      const entitiesOverrideData = await parseJsonSafe<any>(entitiesOverrideRes);

      const mergedLayoutLayers: Array<{
        objects: Array<{ type: string }>;
        tilemaps?: Array<{ type: string }>;
      }> = [];

      const appendLayoutLayer = (
        layer: { objects?: Array<{ type: string }>; tilemaps?: Array<{ type: string }> } | undefined
      ) => {
        if (!layer) return;
        const objects = Array.isArray(layer.objects)
          ? layer.objects
              .filter((obj): obj is { type: string } => !!obj && typeof obj.type === 'string')
              .map((obj) => ({ type: obj.type }))
          : [];
        const tilemaps = Array.isArray(layer.tilemaps)
          ? layer.tilemaps
              .filter((tm): tm is { type: string } => !!tm && typeof tm.type === 'string')
              .map((tm) => ({ type: tm.type }))
          : [];
        if (objects.length === 0 && tilemaps.length === 0) return;
        mergedLayoutLayers.push({
          objects,
          ...(tilemaps.length > 0 ? { tilemaps } : {}),
        });
      };

      for (const layer of layoutData?.layers ?? []) appendLayoutLayer(layer);
      for (const layer of layoutOverrideData?.layers ?? []) appendLayoutLayer(layer);

      const mergedLayout = mergedLayoutLayers.length > 0 ? { layers: mergedLayoutLayers } : null;

      const rawOverrideEntities =
        entitiesOverrideData?.entities ?? entitiesOverrideData?.additions?.entities ?? [];
      const mergedEntitiesList = [
        ...(entitiesData?.entities ?? []),
        ...(Array.isArray(rawOverrideEntities) ? rawOverrideEntities : []),
      ]
        .filter(
          (entity): entity is { entityId: string; texture?: string } =>
            !!entity && typeof entity === 'object' && typeof entity.entityId === 'string'
        )
        .map((entity) => ({
          entityId: entity.entityId,
          ...(typeof entity.texture === 'string' ? { texture: entity.texture } : {}),
        }));

      const mergedEntities =
        mergedEntitiesList.length > 0 ? { entities: mergedEntitiesList } : null;

      const textures = this.deriveTexturesFromLevel(mergedLayout, mergedEntities);

      for (const texture of textures) {
        if (this.destroyed || loadVersion !== this.textureLoadVersion) return;
        if (this.loadedTextures.has(texture.key)) continue;

        try {
          await Assets.load({
            alias: texture.key,
            src: `/assets/${texture.path}`,
          });
          this.loadedTextures.add(texture.key);
        } catch {
          // Texture might not exist
        }
      }

      if (this.destroyed || loadVersion !== this.textureLoadVersion) return;

      // Refresh objects after loading textures
      const state = useEditorStore.getState();
      this.updateObjects(state.objects, state.entities, state.mode);
    } catch {
      // Level data might not exist
    }
  }

  private deriveTexturesFromLevel(
    layout: {
      layers: Array<{ objects: Array<{ type: string }>; tilemaps?: Array<{ type: string }> }>;
    } | null,
    entities: { entities: Array<{ entityId: string; texture?: string }> } | null
  ): Array<{ key: string; path: string }> {
    const TEXTURE_PATHS: Record<string, string> = {
      // Mapeo actualizado con decoraciones y entidades
      tiledbackground: 'game/backgrounds/tiledbackground.png',
      airbackground6: 'game/backgrounds/airbackground6.png',
      mountainbackground4: 'game/backgrounds/mountainbackground4.png',
      bgmazorcas: 'game/backgrounds/bgmazorcas.png',
      decorationmountain: 'game/backgrounds/decorationmountain.png',
      tiledfloor: 'game/environment/tiledfloor.png',
      tmtreegreen: 'game/environment/tmtreegreen.png',
      tmtreeorange: 'game/environment/tmtreeorange.png',
      tmvalla: 'game/environment/tmvalla.png',
      tree1: 'game/environment/tree1.png',
      tree2: 'game/environment/tree2.png',
      rock1: 'game/environment/rock1.png',
      overbridge: 'game/environment/overbridge.png',
      destructablehay: 'game/environment/destructablehay.png',
      house1: 'game/environment/house1.png',
      house2: 'game/environment/house2.png',
      farm1: 'game/environment/farm1.png',
      sprite2: 'game/effects/sprite2.png',
      sprite3: 'game/effects/sprite3.png',

      // Entidades
      cow1: 'game/entities/cow1.png',
      human1: 'game/entities/human1.png',
      human2: 'game/entities/human2.png',
      human3: 'game/entities/human3.png',
      bat1: 'game/entities/bat1.png',
      plasmastore: 'game/entities/plasmastore.png',
      doublelaserstore: 'game/entities/doublelaserstore.png',
      hearthstore: 'game/entities/hearthstore.png',
      cannonbase: 'game/entities/cannonbase.png',
      portal1: 'game/entities/portal1.png',
    };

    const textureSet = new Set<string>();

    if (layout?.layers) {
      for (const layer of layout.layers) {
        for (const obj of layer.objects) {
          textureSet.add(normalizeAssetKey(obj.type));
        }
        if (layer.tilemaps) {
          for (const tm of layer.tilemaps) {
            textureSet.add(normalizeAssetKey(tm.type));
          }
        }
      }
    }

    if (entities?.entities) {
      for (const entity of entities.entities) {
        textureSet.add(normalizeAssetKey(entity.texture || entity.entityId));
      }
    }

    const result: Array<{ key: string; path: string }> = [];
    for (const key of textureSet) {
      const path = TEXTURE_PATHS[key];
      if (path) {
        result.push({ key, path });
      } else {
        result.push({ key, path: `game/spritesheets/${key}.png` });
      }
    }

    return result;
  }

  updateObjects(objects: EditorObject[], entities: EditorEntity[], mode: EditorMode) {
    if (!this.initialized) return;
    void mode;

    const { layerVisibility, entityLayerAssignments } = useEditorStore.getState();

    // Limpiar sprites existentes
    this.objectsContainer.removeChildren();
    this.entitiesContainer.removeChildren();
    this.sprites.clear();
    this.selectionGraphics.clear();
    this.boundsGraphics.clear();

    // Siempre renderizar decoraciones (objetos)
    const layerOrder = this.levelData?.layers.map((l) => l.name) ?? [];
    const sortedObjects = [...objects].sort((a, b) => {
      const aIndex = layerOrder.indexOf(a.layerName);
      const bIndex = layerOrder.indexOf(b.layerName);
      return aIndex - bIndex;
    });
    for (const obj of sortedObjects) {
      const isVisible = layerVisibility[obj.layerName] ?? true;
      // Decorations must remain clickable in entities mode so click can auto-switch to layouts mode.
      const isInteractive = isVisible;
      this.createObjectSprite(obj, isInteractive);

      // Apply visibility state from layerVisibility
      const sprite = this.sprites.get(obj.id);
      const bounds = this.boundsGraphics.get(obj.id);
      if (sprite) {
        sprite.visible = isVisible;
      }
      if (bounds) {
        bounds.visible = isVisible;
      }
    }

    // Siempre renderizar entidades
    for (const entity of entities) {
      this.createEntitySprite(entity);

      const entityLayer = entityLayerAssignments[entity.id];
      const isVisible = entityLayer ? (layerVisibility[entityLayer] ?? true) : true;
      const sprite = this.sprites.get(entity.id);
      const bounds = this.boundsGraphics.get(entity.id);
      if (sprite) {
        sprite.visible = isVisible;
        // Entities must remain clickable in layouts mode so click can auto-switch to entities mode.
        const isInteractive = isVisible;
        sprite.eventMode = isInteractive ? 'static' : 'none';
        sprite.cursor = isInteractive ? 'pointer' : 'default';
      }
      if (bounds) {
        bounds.visible = isVisible;
      }
    }
  }

  private createObjectSprite(obj: EditorObject, isInteractive: boolean) {
    const data = obj.data;
    const textureKey = normalizeAssetKey(data.type);

    let sprite: Sprite;

    const texture = Assets.get(textureKey);
    if (texture) {
      sprite = new Sprite(texture);
    } else {
      this.loadTextureOnDemand(textureKey, resolveDecorationImage(data.type));
      sprite = this.createPlaceholderSprite(data.width, data.height, 0xe94560);
    }

    sprite.x = data.x;
    sprite.y = data.y;
    sprite.width = data.width;
    sprite.height = data.height;

    if (data.originX !== undefined) {
      sprite.anchor.x = data.originX;
    }
    if (data.originY !== undefined) {
      sprite.anchor.y = data.originY;
    }
    if (data.angle !== undefined) {
      sprite.rotation = data.angle;
    }

    const objFw = sprite.texture.frame.width;
    const objFh = sprite.texture.frame.height;
    sprite.hitArea = new Rectangle(
      -sprite.anchor.x * objFw,
      -sprite.anchor.y * objFh,
      objFw,
      objFh
    );

    sprite.eventMode = isInteractive ? 'static' : 'none';
    sprite.cursor = isInteractive ? 'pointer' : 'default';
    (sprite as { objectId?: string }).objectId = obj.id;

    sprite.on('pointerdown', (e: FederatedPointerEvent) => {
      if (!isInteractive) return;
      if (e.button !== 0) return;
      if (!this.isOpaquePointerHit(sprite, e)) return;

      const store = useEditorStore.getState();

      // If user clicks a decoration while in entities mode, switch mode automatically.
      if (store.mode !== 'layouts') {
        store.setMode('layouts');
      }

      if (store.activeLayer !== obj.layerName) {
        store.setActiveLayer(obj.layerName);
      }

      // Force drag to target only the object under the mouse.
      store.selectObject(obj.id, false);

      // Start drag
      this.isDragging = true;
      this.dragStartWorld = this.screenToWorld(e.globalX, e.globalY);
      this.dragStartPositions.clear();

      // Seed with the clicked object using the current sprite position.
      this.dragStartPositions.set(obj.id, { x: sprite.x, y: sprite.y });
    });

    this.sprites.set(obj.id, sprite);
    this.objectsContainer.addChild(sprite);

    const boundsGraphics = new Graphics();
    boundsGraphics.eventMode = 'none';
    this.drawSpriteBounds(boundsGraphics, sprite, 0x4ade80, 0.22);
    this.boundsGraphics.set(obj.id, boundsGraphics);
    this.objectsContainer.addChild(boundsGraphics);

    const selGraphics = new Graphics();
    selGraphics.eventMode = 'none';
    selGraphics.visible = false;
    this.selectionGraphics.set(obj.id, selGraphics);
    this.objectsContainer.addChild(selGraphics);
  }

  private createEntitySprite(entity: EditorEntity) {
    const data = entity.data;
    // Algunas entidades (p.ej. de additions) no tienen campo texture; usar entityId como fallback
    const sourceKey = data.texture ?? data.entityId ?? '';
    const textureKey = normalizeAssetKey(sourceKey);

    let sprite: Sprite;

    const texture = Assets.get(textureKey);
    if (texture) {
      sprite = new Sprite(texture);
    } else {
      this.loadTextureOnDemand(textureKey, resolveEntityImage(sourceKey));
      sprite = this.createPlaceholderSprite(data.width, data.height, 0xfbbf24);
    }

    sprite.x = data.x;
    sprite.y = data.y;
    sprite.width = data.width;
    sprite.height = data.height;
    sprite.anchor.set(0.5, 1);

    const entFw = sprite.texture.frame.width;
    const entFh = sprite.texture.frame.height;
    sprite.hitArea = new Rectangle(
      -sprite.anchor.x * entFw,
      -sprite.anchor.y * entFh,
      entFw,
      entFh
    );

    sprite.eventMode = 'static';
    sprite.cursor = 'pointer';
    (sprite as { objectId?: string }).objectId = entity.id;

    sprite.on('pointerdown', (e: FederatedPointerEvent) => {
      if (e.button !== 0) return;
      if (!this.isOpaquePointerHit(sprite, e)) return;

      const store = useEditorStore.getState();

      // If user clicks an entity while in layouts mode, switch mode automatically.
      if (store.mode !== 'entities') {
        store.setMode('entities');
      }

      // Force drag to target only the entity under the mouse.
      store.selectObject(entity.id, false);

      this.isDragging = true;
      this.dragStartWorld = this.screenToWorld(e.globalX, e.globalY);
      this.dragStartPositions.clear();

      // Seed with the clicked entity using the current sprite position.
      this.dragStartPositions.set(entity.id, { x: sprite.x, y: sprite.y });
    });

    this.sprites.set(entity.id, sprite);
    this.entitiesContainer.addChild(sprite);

    const boundsGraphics = new Graphics();
    boundsGraphics.eventMode = 'none';
    this.drawSpriteBounds(boundsGraphics, sprite, 0x60a5fa, 0.26);
    this.boundsGraphics.set(entity.id, boundsGraphics);
    this.entitiesContainer.addChild(boundsGraphics);

    const selGraphics = new Graphics();
    selGraphics.eventMode = 'none';
    selGraphics.visible = false;
    this.selectionGraphics.set(entity.id, selGraphics);
    this.entitiesContainer.addChild(selGraphics);
  }

  private drawSpriteBounds(graphics: Graphics, sprite: Sprite, color: number, alpha: number) {
    graphics.clear();

    const localX = -sprite.anchor.x * sprite.width;
    const localY = -sprite.anchor.y * sprite.height;
    const gridStep = this.getSpriteGridStep(sprite.width, sprite.height);

    graphics.rect(localX, localY, sprite.width, sprite.height);
    graphics.fill({ color, alpha: 0.06 });

    graphics.rect(localX, localY, sprite.width, sprite.height);
    graphics.stroke({ width: 2, color, alpha: alpha + 0.18 });

    for (let x = gridStep; x < sprite.width; x += gridStep) {
      graphics.moveTo(localX + x, localY);
      graphics.lineTo(localX + x, localY + sprite.height);
    }

    for (let y = gridStep; y < sprite.height; y += gridStep) {
      graphics.moveTo(localX, localY + y);
      graphics.lineTo(localX + sprite.width, localY + y);
    }

    graphics.stroke({ width: 1.2, color, alpha: alpha + 0.06 });

    graphics.x = sprite.x;
    graphics.y = sprite.y;
    graphics.rotation = sprite.rotation;
  }

  private getSpriteGridStep(width: number, height: number): number {
    const maxLines = 24;
    const baseSteps = [16, 32, 64, 128, 256, 512, 1024, 2048, 4096];

    for (const step of baseSteps) {
      if (width / step <= maxLines && height / step <= maxLines) {
        return step;
      }
    }

    return baseSteps[baseSteps.length - 1] ?? 4096;
  }

  private createPlaceholderSprite(width: number, height: number, color: number): Sprite {
    const graphics = new Graphics();
    graphics.rect(0, 0, width, height);
    graphics.fill({ color, alpha: 0.5 });
    graphics.stroke({ width: 2, color });

    const texture = this.app.renderer.generateTexture(graphics);
    return new Sprite(texture);
  }

  private loadTextureOnDemand(textureKey: string, imagePath: string) {
    if (!textureKey || this.loadedTextures.has(textureKey) || this.loadingTextures.has(textureKey))
      return;

    this.loadingTextures.add(textureKey);
    Assets.load({
      alias: textureKey,
      src: imagePath,
    })
      .then(() => {
        if (this.destroyed) return;
        this.loadedTextures.add(textureKey);
        const state = useEditorStore.getState();
        this.updateObjects(state.objects, state.entities, state.mode);
      })
      .catch(() => {
        // Missing assets keep the visible placeholder so the dropped object is still selectable.
      })
      .finally(() => {
        this.loadingTextures.delete(textureKey);
      });
  }

  private isOpaquePointerHit(sprite: Sprite, event: FederatedPointerEvent): boolean {
    const textureData = this.getTextureAlphaData(sprite);
    if (!textureData) {
      return true;
    }

    // getLocalPosition returns texture-pixel coordinates (local space includes scale).
    const local = event.getLocalPosition(sprite);
    const tw = textureData.width;
    const th = textureData.height;
    const pixelX = Math.floor(local.x + sprite.anchor.x * tw);
    const pixelY = Math.floor(local.y + sprite.anchor.y * th);

    if (pixelX < 0 || pixelY < 0 || pixelX >= tw || pixelY >= th) {
      return false;
    }

    const alpha = textureData.data[(pixelY * tw + pixelX) * 4 + 3] ?? 0;
    return alpha > 16;
  }

  private getTextureAlphaData(
    sprite: Sprite
  ): { data: Uint8ClampedArray; width: number; height: number } | null {
    const frame = sprite.texture.frame;
    const cacheKey = `${sprite.texture.label || sprite.texture.uid.toString()}:${frame.x}:${frame.y}:${frame.width}:${frame.height}`;
    const cached = this.alphaBoundsCache.get(cacheKey);
    if (cached) {
      return {
        data: cached,
        width: Math.max(1, Math.floor(frame.width)),
        height: Math.max(1, Math.floor(frame.height)),
      };
    }

    const source = (
      sprite.texture.source as { resource?: { source?: CanvasImageSource } } | undefined
    )?.resource?.source;
    if (!source) {
      return null;
    }

    const width = Math.max(1, Math.floor(frame.width || sprite.texture.width));
    const height = Math.max(1, Math.floor(frame.height || sprite.texture.height));
    if (!width || !height) {
      return null;
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) {
      return null;
    }

    context.clearRect(0, 0, width, height);
    context.drawImage(source, frame.x, frame.y, width, height, 0, 0, width, height);

    const imageData = context.getImageData(0, 0, width, height).data;
    this.alphaBoundsCache.set(cacheKey, imageData);

    return { data: imageData, width, height };
  }

  setSelection(selectedIds: string[]) {
    for (const [id, graphics] of this.selectionGraphics) {
      const isSelected = selectedIds.includes(id);
      graphics.visible = isSelected;

      if (isSelected) {
        this.updateSelectionGraphicsForSprite(id, graphics);
      }
    }
  }

  private updateSelectionGraphics() {
    const store = useEditorStore.getState();
    for (const [id, graphics] of this.boundsGraphics) {
      const sprite = this.sprites.get(id);
      if (sprite && sprite.visible) {
        const isEntity = store.entities.some((entity) => entity.id === id);
        const color = isEntity ? 0x60a5fa : 0x4ade80;
        const alpha = isEntity ? 0.26 : 0.22;
        this.drawSpriteBounds(graphics, sprite, color, alpha);
      }
    }
    for (const id of store.selectedIds) {
      const graphics = this.selectionGraphics.get(id);
      if (graphics) {
        this.updateSelectionGraphicsForSprite(id, graphics);
      }
    }
  }

  private updateSelectionGraphicsForSprite(id: string, graphics: Graphics) {
    const sprite = this.sprites.get(id);
    if (sprite) {
      graphics.clear();

      const bounds = sprite.getBounds();
      graphics.rect(bounds.x - sprite.x, bounds.y - sprite.y, bounds.width, bounds.height);
      graphics.stroke({ width: 2, color: 0xe94560 });

      graphics.x = sprite.x;
      graphics.y = sprite.y;
    }
  }

  setLayerVisibility(visibility: Record<string, boolean>) {
    const state = useEditorStore.getState();
    for (const [id, sprite] of this.sprites) {
      const obj = state.objects.find((o) => o.id === id);
      if (obj) {
        const isVisible = visibility[obj.layerName] ?? true;
        sprite.visible = isVisible;

        // Also update interactivity: if layer is hidden, make sprite non-interactive
        sprite.eventMode = isVisible ? 'static' : 'none';
        sprite.cursor = isVisible ? 'pointer' : 'default';

        // Update bounds visibility to match sprite visibility
        const bounds = this.boundsGraphics.get(id);
        if (bounds) {
          bounds.visible = isVisible;
        }
        continue;
      }

      const entityLayer = state.entityLayerAssignments[id];
      if (entityLayer) {
        const isVisible = visibility[entityLayer] ?? true;
        sprite.visible = isVisible;
        // Keep entities clickable whenever visible (mode switch happens on entity pointerdown).
        sprite.eventMode = isVisible ? 'static' : 'none';
        sprite.cursor = isVisible ? 'pointer' : 'default';

        const bounds = this.boundsGraphics.get(id);
        if (bounds) {
          bounds.visible = isVisible;
        }
      }
    }
  }

  getSprite(id: string): Sprite | undefined {
    return this.sprites.get(id);
  }

  resize(_width: number, _height: number) {
    // Handled by ResizeObserver
  }

  destroy() {
    // Stop resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    // Clear containers
    this.initialized = false;
    this.destroyed = true;
    this.textureLoadVersion += 1;
    this.sprites.clear();
    this.selectionGraphics.clear();
    this.boundsGraphics.clear();
    this.alphaBoundsCache.clear();

    // Destroy Pixi app
    try {
      this.app.destroy(true, { children: true });
    } catch {
      // Ignore destroy errors
    }
  }
}
